import { useEffect } from "react";
import { publicMediaSrc, toConfiguredAbsoluteUrl } from "../config/api";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.categoryDetailSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.categoryDetailSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.categoryDetailSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.categoryDetailSeo = "true";
}

function upsertJsonLd(data) {
  const id = "category-detail-jsonld";
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

export function categoryPageTitle(category, contact) {
  const seoTitle = cleanText(category?.seoTitle);
  if (seoTitle) return seoTitle;
  const name = cleanText(category?.categoryName);
  const siteName = cleanText(contact?.siteName);
  if (name && siteName) return `${name} Supplier Malaysia | ${siteName}`;
  if (name) return `${name} Supplier Malaysia`;
  if (siteName) return `Product Category | ${siteName}`;
  return "Product Category";
}

export function categoryPageDescription(category, contact) {
  const seoDescription = cleanText(category?.seoDescription);
  if (seoDescription) return seoDescription;
  const description = cleanText(category?.description);
  if (description) return description;
  const name = cleanText(category?.categoryName);
  const siteName = cleanText(contact?.siteName);
  if (name && siteName) {
    return `Browse ${name} supplied by ${siteName} for contractors and businesses across Malaysia.`;
  }
  if (name) return `Browse ${name} for contractors and businesses across Malaysia.`;
  if (siteName) return `Browse products supplied by ${siteName} for contractors and businesses across Malaysia.`;
  return "Browse industrial supplies for contractors and businesses across Malaysia.";
}

function productPublicPath(category, product) {
  const categorySlug = cleanText(category?.slug);
  const productSlug = cleanText(product?.slug);
  if (!categorySlug || !productSlug) return "";
  return `/category/${categorySlug}/${productSlug}`;
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function CategoryDetailSeo({ contact, category, products }) {
  useEffect(() => {
    if (!category?.slug || category.isPublished === false) return undefined;

    const name = cleanText(category.categoryName) || "Category";
    const siteName = cleanText(contact?.siteName);
    const pageTitle = categoryPageTitle(category, contact);
    const pageDescription = categoryPageDescription(category, contact);
    const canonicalUrl = toConfiguredAbsoluteUrl(`/category/${category.slug}`);
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const listUrl = toConfiguredAbsoluteUrl("/category");
    const imagePath = publicMediaSrc(category.categoryPhoto);
    const imageUrl = imagePath ? toConfiguredAbsoluteUrl(imagePath) : "";
    const imageAlt = cleanText(category.categoryPhotoAlt);

    document.title = pageTitle;
    upsertMeta("name", "description", pageDescription);
    upsertLink("canonical", canonicalUrl);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", pageDescription);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? imageAlt : "");
    upsertMeta("property", "og:site_name", siteName);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", pageDescription);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? imageAlt : "");

    const breadcrumbId = canonicalUrl ? `${canonicalUrl}#breadcrumb` : undefined;
    const pageId = canonicalUrl ? `${canonicalUrl}#webpage` : undefined;
    const breadcrumbList = {
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
        { "@type": "ListItem", position: 2, name: "Product Categories", item: listUrl },
        { "@type": "ListItem", position: 3, name, item: canonicalUrl },
      ],
    };
    const collectionPage = {
      "@type": "CollectionPage",
      "@id": pageId,
      name,
      description: pageDescription,
      url: canonicalUrl,
      primaryImageOfPage: imageUrl
        ? { "@type": "ImageObject", url: imageUrl, caption: imageAlt || undefined }
        : undefined,
      breadcrumb: breadcrumbId ? { "@id": breadcrumbId } : breadcrumbList,
    };
    const graph = [collectionPage, breadcrumbList];
    const list = Array.isArray(products) ? products.filter((item) => cleanText(item?.productName)) : [];
    if (list.length) {
      graph.push({
        "@type": "ItemList",
        itemListElement: list.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: cleanText(item.productName),
          url: toConfiguredAbsoluteUrl(productPublicPath(category, item)),
          image: toConfiguredAbsoluteUrl(publicMediaSrc(item.image)),
        })),
      });
    }

    upsertJsonLd(prune({ "@context": "https://schema.org", "@graph": graph }));

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      document.head.querySelectorAll("[data-category-detail-seo='true']").forEach((el) => el.remove());
      const script = document.getElementById("category-detail-jsonld");
      if (script) script.remove();
    };
  }, [contact, category, products]);

  return null;
}

export default CategoryDetailSeo;
